import React, { useEffect, useState } from "react";
import BuildIcon from "@mui/icons-material/Build";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import DictionaryApiService from "../../admin/system/integrate/DictionaryApiService";
import Logger from "../../services/Logger";
import { validateFilledCoordinatesCellConflicts, validateWordPositionValues } from "../../admin/pzl_common/filledCoordinatesValidation";

function getWordsFromPuzzle(gridTpl) {
    if (!gridTpl?.filledCoordinates) return [];
    try {
        const coords = JSON.parse(gridTpl.filledCoordinates);
        return (coords || [])
            .filter((c) => c.text && typeof c.text === "string")
            .map((c) => c.text.trim().toLowerCase())
            .filter((w) => w.length > 0);
    } catch {
        return [];
    }
}

/**
 * Validation popup that checks the puzzle for cell conflicts, word/position mismatches,
 * duplicate words, and banned words. When resolvable errors exist (conflicts, duplicates,
 * mismatches), shows a Resolve button that opens ResolvePuzzleDialog directly.
 */
export default function ValidationDialog({ open, onClose, onResolve, gridTpl }) {
    const [validationLoading, setValidationLoading] = useState(false);
    const [cellConflicts, setCellConflicts] = useState([]);
    const [wordMismatches, setWordMismatches] = useState([]);
    const [duplicateWords, setDuplicateWords] = useState([]);
    const [bannedWords, setBannedWords] = useState([]);

    const runValidation = async () => {
        setCellConflicts([]);
        setWordMismatches([]);
        setDuplicateWords([]);
        setBannedWords([]);
        const coords = (() => {
            if (!gridTpl?.filledCoordinates) return [];
            try {
                return JSON.parse(gridTpl.filledCoordinates) || [];
            } catch {
                return [];
            }
        })();
        const cellResult = validateFilledCoordinatesCellConflicts(coords);
        if (!cellResult.valid && cellResult.conflicts?.length > 0) {
            setCellConflicts(cellResult.conflicts);
            setValidationLoading(false);
            return;
        }
        const wordResult = validateWordPositionValues(coords);
        if (!wordResult.valid && wordResult.mismatches?.length > 0) {
            setWordMismatches(wordResult.mismatches);
            setValidationLoading(false);
            return;
        }
        const words = getWordsFromPuzzle(gridTpl);
        if (words.length === 0) {
            setValidationLoading(false);
            return;
        }
        setValidationLoading(true);
        try {
            const countByWord = {};
            words.forEach((w) => { countByWord[w] = (countByWord[w] || 0) + 1; });
            const duplicates = Object.entries(countByWord)
                .filter(([, count]) => count > 1)
                .map(([word]) => word);
            setDuplicateWords(duplicates);
            if (duplicates.length > 0) {
                setValidationLoading(false);
                return;
            }
            const uniqueWords = [...new Set(words)];
            const language = gridTpl?.language || "english";
            const result = await DictionaryApiService.checkBannedWords(uniqueWords, language, gridTpl?.isKids || false, gridTpl?.difficultyLevel);
            const banned = result?.bannedWords && result.bannedWords.length > 0 ? result.bannedWords : [];
            setBannedWords(banned);
        } catch (err) {
            Logger.error("Error validating puzzle:", err);
            setBannedWords(["Validation failed. Please try again."]);
        } finally {
            setValidationLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            runValidation();
        } else {
            setCellConflicts([]);
            setWordMismatches([]);
            setDuplicateWords([]);
            setBannedWords([]);
        }
    }, [open]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Validation</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Validates the puzzle for cell conflicts, word/position mismatches, duplicates, and banned words.
                    Use Resolve to fix conflicts, duplicates, and mismatches.
                </Typography>
                {validationLoading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="text.secondary">
                            Validating puzzle... Checking cell positions... Checking word/position values... Checking for duplicate words... Checking for banned words...
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        {cellConflicts.length > 0 ? (
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "error.main", mt: 0.5, flexShrink: 0 }} />
                                <Box>
                                    <Typography variant="body2" color="error" sx={{ fontWeight: 600 }}>
                                        Cell conflicts: same (row, col) with different values ({cellConflicts.length} cell(s)):
                                    </Typography>
                                    {cellConflicts.slice(0, 10).map((c, i) => (
                                        <Typography key={i} variant="body2" color="error">
                                            Row {c.row}, Col {c.col}: {c.values.join(" vs ")}
                                        </Typography>
                                    ))}
                                    {cellConflicts.length > 10 && (
                                        <Typography variant="body2" color="error">
                                            ... and {cellConflicts.length - 10} more
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        ) : (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "success.main", flexShrink: 0 }} />
                                <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                                    ✓ No cell conflicts
                                </Typography>
                            </Box>
                        )}
                        {wordMismatches.length > 0 ? (
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "error.main", mt: 0.5, flexShrink: 0 }} />
                                <Box>
                                    <Typography variant="body2" color="error" sx={{ fontWeight: 600 }}>
                                        Word/position value mismatch ({wordMismatches.length} word(s)): positions do not spell the word
                                    </Typography>
                                    {wordMismatches.slice(0, 10).map((m, i) => (
                                        <Typography key={i} variant="body2" color="error">
                                            &quot;{m.word}&quot;: expected &quot;{m.expected}&quot;, got values &quot;{m.actual}&quot;
                                        </Typography>
                                    ))}
                                    {wordMismatches.length > 10 && (
                                        <Typography variant="body2" color="error">
                                            ... and {wordMismatches.length - 10} more
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        ) : (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "success.main", flexShrink: 0 }} />
                                <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                                    ✓ No word/position value mismatches
                                </Typography>
                            </Box>
                        )}
                        {duplicateWords.length > 0 ? (
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "error.main", mt: 0.5, flexShrink: 0 }} />
                                <Box>
                                    <Typography variant="body2" color="error" sx={{ fontWeight: 600 }}>
                                        Duplicate words found ({duplicateWords.length}):
                                    </Typography>
                                    <Typography variant="body2" color="error">
                                        {duplicateWords.join(", ")}
                                    </Typography>
                                </Box>
                            </Box>
                        ) : (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "success.main", flexShrink: 0 }} />
                                <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                                    ✓ No duplicate words
                                </Typography>
                            </Box>
                        )}
                        {!validationLoading && bannedWords.length > 0 ? (
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "error.main", mt: 0.5, flexShrink: 0 }} />
                                <Box>
                                    <Typography variant="body2" color="error" sx={{ fontWeight: 600 }}>
                                        Banned words found ({bannedWords.length}):
                                    </Typography>
                                    <Typography variant="body2" color="error">
                                        {bannedWords.join(", ")}
                                    </Typography>
                                </Box>
                            </Box>
                        ) : !validationLoading && duplicateWords.length === 0 ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "success.main", flexShrink: 0 }} />
                                <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                                    ✓ No banned words
                                </Typography>
                            </Box>
                        ) : null}
                        {!validationLoading && cellConflicts.length === 0 && wordMismatches.length === 0 && duplicateWords.length === 0 && bannedWords.length === 0 && getWordsFromPuzzle(gridTpl).length > 0 && (
                            <Typography variant="body2" color="success.main" sx={{ mt: 0.5, fontWeight: 600 }}>
                                ✓ Puzzle validation passed
                            </Typography>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 2, pb: 2 }}>
                {onResolve && (cellConflicts.length > 0 || wordMismatches.length > 0 || duplicateWords.length > 0) && !validationLoading && (
                    <Button
                        onClick={() => {
                            onClose();
                            const resolveType = cellConflicts.length > 0 ? "cellConflicts" : wordMismatches.length > 0 ? "wordMismatches" : "duplicates";
                            onResolve(resolveType);
                        }}
                        variant="outlined"
                        color="primary"
                        startIcon={<BuildIcon fontSize="small" />}
                    >
                        Resolve
                    </Button>
                )}
                <Box sx={{ flex: 1 }} />
                <Button onClick={onClose} variant="contained" color="primary">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
